import React, {useState} from "react"
import {compact, get, isEmpty} from "lodash"
import {UserOutlined} from "@ant-design/icons"
import {Popover, Tooltip} from "antd"
import {getColorContrast, getFullName} from "helpers"
import {EventPreviewContent} from "./EventPreviewContent"
import styles from "./EventContent.module.css"
import moment from "moment-timezone"
import {useModel} from "hooks"
import {CalendarEventType} from "types/calendar"
import {isMonthView} from "../../calendarViews"

const SLOT_HEIGHT_PX = 45
const DETAIL_ROW_HEIGHT_PX = 16

export function EventContent(props) {
    const [isShowEventPreview, setIsShowEventPreview] = useState(false)
    const model = useModel()
    const {
        updateViewTo,
        eventInfo,
        clickInfo,
        handleShowEventPreview,
        isShowGoing,
        eventOptions,
        isWorkScheduleView,
        linkedCalendars
    } = props
    const backgroundColor = isMonthView(updateViewTo)
        ? "#fff"
        : get(eventInfo, "event._def.ui.backgroundColor", "#1e90ff")

    const closeEventPreview = () => {
        setIsShowEventPreview(false)
        setTimeout(() => {
            handleShowEventPreview(false)
            // To trigger handleDateClick event happen before close
        }, 300)
        clickInfo?.el?.parentElement?.classList?.remove(styles.highlightEventPreviewWrap)
        clickInfo?.el?.classList?.remove(styles.highlightEventPreview)
    }

    const onEventPreviewChange = (visible) => {
        if (!visible) {
            clickInfo?.el?.parentElement?.classList?.remove(styles.highlightEventPreviewWrap)
            clickInfo?.el?.classList?.remove(styles.highlightEventPreview)
        } else {
            clickInfo?.el?.parentElement?.classList?.add(styles.highlightEventPreviewWrap)
            clickInfo?.el?.classList?.add(styles.highlightEventPreview)
        }
        setIsShowEventPreview(visible)
        setTimeout(() => {
            handleShowEventPreview(visible)
            // To trigger handleDateClick event happen before close
        }, 300)
    }

    const renderEventPreviewContent = (eventInfo) => {
        return (
            <EventPreviewContent
                eventInfo={eventInfo}
                isShowGoing={isShowGoing}
                eventOptions={eventOptions}
                linkedCalendars={linkedCalendars}
                closeEventPreview={closeEventPreview}
                isWorkScheduleView={isWorkScheduleView}
            />
        )
    }

    const renderMonthView = () => {
        return (
            <div className={styles.eventMonthView}>
                <div className={styles.dot} style={{background: eventInfo.event.backgroundColor}}></div>
                <div className={styles.eventTitleMonthView} style={{color: getColorContrast(backgroundColor).color}}>
                    <span>{eventInfo.event.title}</span>
                </div>
            </div>
        )
    }

    const renderInvitees = (invitedUsersLabel: string, color: string) => (
        <Tooltip title={invitedUsersLabel}>
            <div className={styles.eventInvitees} style={{color}}>
                <UserOutlined className={styles.eventInviteesIcon} />
                <span className={styles.eventInviteesText}>{invitedUsersLabel}</span>
            </div>
        </Tooltip>
    )

    const renderOtherView = () => {
        const format = model.getUserTimeFormat()
        const {schedule_suffix, moduleTitle, subModuleTitle, type, invitedUsers, allDay} = eventInfo.event.extendedProps
        const courseScheduleInfo = [schedule_suffix, moduleTitle, subModuleTitle].filter((item) => item).join(", ")
        const durationMinutes = moment(eventInfo.event.end).diff(eventInfo.event.start, "minutes")
        const color = getColorContrast(backgroundColor).color
        const timePreview =
            type === CalendarEventType.EndTermDate
                ? moment(eventInfo.event.start).endOf("date")
                : moment(eventInfo.event.start)
        const title = eventInfo.event.title
        const isAllDay = allDay || eventInfo.event.allDay
        const timeLabel = isAllDay ? null : timePreview.format(format)
        const invitedUsersLabel = compact(invitedUsers)
            .map((user) => getFullName(user))
            .filter(Boolean)
            .join(", ")
        const hasInvitees = !isEmpty(invitedUsersLabel)

        const eventHeight = (Math.max(durationMinutes, 0) / 60) * SLOT_HEIGHT_PX
        const maxRows = Math.max(1, Math.floor(eventHeight / DETAIL_ROW_HEIGHT_PX))
        const isCompact = maxRows <= 1

        const detailRows = compact([
            hasInvitees && <React.Fragment key="invitees">{renderInvitees(invitedUsersLabel, color)}</React.Fragment>,
            timeLabel && (
                <p key="time" className={styles.eventMeta} style={{color}} title={timeLabel}>
                    {timeLabel}
                </p>
            ),
            courseScheduleInfo && (
                <p key="course" className={styles.eventMeta} style={{color}} title={courseScheduleInfo}>
                    {courseScheduleInfo}
                </p>
            )
        ]).slice(0, maxRows - 1)

        return (
            <div className={`${styles.eventContent} ${isCompact ? styles.eventContentCompact : ""}`}>
                {title && (
                    <p className={styles.eventTitle} style={{color}} title={title}>
                        {title}
                    </p>
                )}
                {detailRows}
            </div>
        )
    }

    return (
        <Popover
            overlayClassName="event-content-preview"
            visible={isShowEventPreview}
            content={() => renderEventPreviewContent(eventInfo.event)}
            trigger="click"
            onVisibleChange={onEventPreviewChange}
            placement="left">
            {isMonthView(updateViewTo) ? renderMonthView() : renderOtherView()}
        </Popover>
    )
}
